import { Pencil, MessageSquare, Forward } from 'lucide-react';
import { Tooltip } from '@/components/ui';
import { useI18n } from '@/lib/i18n';

interface ConversationActionsProps {
  canEdit: boolean;
  canThread: boolean;
  canForward: boolean;
  onEdit?: () => void;
  onThread?: () => void;
  onForward?: () => void;
}

/**
 * Icon-only hover actions shown at the top-right of a conversation/note card
 * (Freshdesk-style "Edit" / "Start a thread" / "Forward"). Each icon renders
 * only when its permission is granted, matching the reference: agents see
 * Edit + Start a thread; only admins/super-admins additionally see Forward.
 */
export function ConversationActions({ canEdit, canThread, canForward, onEdit, onThread, onForward }: ConversationActionsProps) {
  const { t } = useI18n();
  if (!canEdit && !canThread && !canForward) return null;
  return (
    <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
      {canEdit && (
        <Tooltip label={t('tp.convActions.edit')}>
          <button
            type="button"
            onClick={onEdit}
            aria-label={t('tp.convActions.edit')}
            className="rounded p-1.5 text-ink-subtle hover:bg-surface-hover hover:text-ink"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </Tooltip>
      )}
      {canThread && (
        <Tooltip label={t('tp.convActions.thread')}>
          <button
            type="button"
            onClick={onThread}
            aria-label={t('tp.convActions.thread')}
            className="rounded p-1.5 text-ink-subtle hover:bg-surface-hover hover:text-ink"
          >
            <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </Tooltip>
      )}
      {canForward && (
        <Tooltip label={t('tp.forward.title')}>
          <button
            type="button"
            onClick={onForward}
            aria-label={t('tp.forward.title')}
            className="rounded p-1.5 text-ink-subtle hover:bg-surface-hover hover:text-ink"
          >
            <Forward className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </Tooltip>
      )}
    </div>
  );
}
